import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Pin, Trash2, ArrowLeft, Send, Plus, Lock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

function timeAgo(iso) {
  try {
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 60) return "just now";
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
  } catch (e) { return ""; }
}

const PROMPTS = ["How many hours have you practiced this week?", "Share a win from today's practice", "Which note still trips you up?"];

export default function Community() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // {thread, replies}
  const [newThread, setNewThread] = useState({ title: "", body: "" });
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const loadThreads = () => {
    setLoading(true);
    api.get("/community/threads").then(({ data }) => setThreads(data.threads || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(loadThreads, []);

  const openThread = async (id) => {
    try { const { data } = await api.get(`/community/threads/${id}`); setSelected(data); } catch (e) { toast.error("Could not open thread."); }
  };

  const createThread = async () => {
    if (!newThread.title.trim() || !newThread.body.trim()) { toast.error("Add a title and a message."); return; }
    setBusy(true);
    try {
      await api.post("/community/threads", { title: newThread.title.trim(), body: newThread.body.trim() });
      toast.success("Posted!");
      setNewThread({ title: "", body: "" });
      loadThreads();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/community/threads/${selected.thread.id}/replies`, { body: reply.trim() });
      setReply("");
      openThread(selected.thread.id);
      loadThreads();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const pin = async (t) => {
    try { await api.post(`/community/threads/${t.id}/pin`, { pinned: !t.pinned }); loadThreads(); if (selected) openThread(t.id); } catch (e) { toast.error("Could not update."); }
  };
  const delThread = async (id) => {
    try { await api.delete(`/community/threads/${id}`); toast.success("Deleted."); setSelected(null); loadThreads(); } catch (e) { toast.error("Could not delete."); }
  };
  const delReply = async (rid) => {
    try { await api.delete(`/community/replies/${rid}`); openThread(selected.thread.id); } catch (e) { toast.error("Could not delete."); }
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 sm:px-8 py-14 lg:py-16">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary"><MessageSquare size={14} /> Community board</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold tracking-tight sm:text-5xl">Practice together, stay accountable</h1>
        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">Start conversations, share your progress and cheer each other on. A friendly place to build the habit.</p>
        <div className="relative mt-8 overflow-hidden rounded-3xl border border-border">
          <img src="/media/jt-performing.webp" alt="Jason Thompson on stage in front of a huge festival crowd" className="h-56 w-full object-cover object-[center_45%] sm:h-72" data-testid="community-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <p className="absolute bottom-4 left-5 font-mono text-[11px] uppercase tracking-widest text-white/90">Every big stage starts with daily practice</p>
        </div>

        {!selected ? (
          <>
            {/* Start a conversation */}
            {user ? (
              <div className="mt-10 rounded-2xl border border-primary/30 bg-card p-6" data-testid="new-thread-form">
                <p className="flex items-center gap-2 font-serif text-xl font-semibold"><Plus size={18} className="text-primary" /> Start a conversation</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PROMPTS.map((p) => (
                    <button key={p} onClick={() => setNewThread((n) => ({ ...n, title: p }))} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary">{p}</button>
                  ))}
                </div>
                <input value={newThread.title} onChange={(e) => setNewThread((n) => ({ ...n, title: e.target.value }))} placeholder="Title" data-testid="new-thread-title" className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                <textarea value={newThread.body} onChange={(e) => setNewThread((n) => ({ ...n, body: e.target.value }))} placeholder="Say something…" rows={3} data-testid="new-thread-body" className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                <Button onClick={createThread} disabled={busy} data-testid="new-thread-submit" className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">{busy ? "Posting…" : "Post"}</Button>
              </div>
            ) : (
              <div className="mt-10 flex flex-col items-start gap-3 rounded-2xl border border-dashed border-border bg-card p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="community-login-prompt">
                <p className="flex items-center gap-2 text-muted-foreground"><Lock size={16} /> Log in to post and reply. Reading is open to everyone.</p>
                <div className="flex gap-2">
                  <Link to="/login"><Button variant="outline" className="rounded-full border-border">Log in</Button></Link>
                  <Link to="/register"><Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Join</Button></Link>
                </div>
              </div>
            )}

            {/* Threads */}
            <div className="mt-8 space-y-3" data-testid="thread-list">
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : threads.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="threads-empty">No conversations yet — be the first to post!</p>
              ) : (
                threads.map((t) => (
                  <div key={t.id} data-testid={`thread-${t.id}`} className="rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary">
                    <div className="flex items-start justify-between gap-3">
                      <button onClick={() => openThread(t.id)} className="flex-1 text-left">
                        <p className="flex items-center gap-2 font-serif text-lg font-semibold">
                          {t.pinned && <Pin size={14} className="text-primary" />} {t.title}
                        </p>
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{t.body}</p>
                        <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{t.author_name} · {t.reply_count} replies · {timeAgo(t.last_activity)}</p>
                      </button>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <button onClick={() => pin(t)} data-testid={`thread-pin-${t.id}`} title="Pin" className={`flex h-8 w-8 items-center justify-center rounded-full border border-border ${t.pinned ? "text-primary" : "text-muted-foreground"} hover:border-primary`}><Pin size={14} /></button>
                          <button onClick={() => delThread(t.id)} data-testid={`thread-delete-${t.id}`} title="Delete" className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground hover:border-red-500 hover:text-red-500"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          /* Thread detail */
          <div className="mt-8" data-testid="thread-detail">
            <button onClick={() => setSelected(null)} data-testid="thread-back" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Back to all conversations</button>
            <div className="mt-4 rounded-2xl border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-serif text-2xl font-bold">{selected.thread.pinned && <Pin size={16} className="text-primary" />}{selected.thread.title}</h2>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{selected.thread.author_name} · {timeAgo(selected.thread.created_at)}</p>
              <p className="mt-4 whitespace-pre-wrap text-foreground">{selected.thread.body}</p>
            </div>

            <div className="mt-6 space-y-3">
              {selected.replies.map((r) => (
                <div key={r.id} data-testid={`reply-${r.id}`} className="rounded-xl border border-border bg-background p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{r.author_name} · {timeAgo(r.created_at)}</p>
                    {isAdmin && <button onClick={() => delReply(r.id)} data-testid={`reply-delete-${r.id}`} className="text-muted-foreground hover:text-red-500"><Trash2 size={13} /></button>}
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm">{r.body}</p>
                </div>
              ))}
              {selected.replies.length === 0 && <p className="text-sm text-muted-foreground">No replies yet.</p>}
            </div>

            {user ? (
              <div className="mt-5 flex gap-2" data-testid="reply-form">
                <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Write a reply…" data-testid="reply-input" className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary" />
                <Button onClick={sendReply} disabled={busy} data-testid="reply-submit" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"><Send size={15} /></Button>
              </div>
            ) : (
              <p className="mt-5 text-sm text-muted-foreground"><Link to="/login" className="text-primary underline">Log in</Link> to reply.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
